// Basic types for the requirements tracking system

export interface Project {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
  sections: Section[];
  overallProgress: number;
}

export interface Section {
  id: string;
  name: string;
  description: string;
  weight: number;
  isCore: boolean;
  completionPercentage: number;
  subsections: Subsection[];
  isExpanded?: boolean;
}

export interface Subsection {
  id: string;
  name: string;
  description: string;
  weight: number;
  completionPercentage: number;
  items: Item[];
  isExpanded?: boolean;
}

export interface Item {
  id: string;
  title: string;
  description: string;
  status: ItemStatus;
  weight: number;
  isRequired: boolean;
  lastModified: Date;
  notes?: string;
  attachments?: Attachment[];
  relatedItems?: string[]; // IDs of related items
}

export enum ItemStatus {
  NotStarted = "NOT_STARTED",
  InProgress = "IN_PROGRESS",
  Completed = "COMPLETED",
  Skipped = "SKIPPED"
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  uploadedAt: Date;
}

export interface WeightProfile {
  id: string;
  name: string;
  description: string;
  projectType: string;
  sectionWeights: Record<string, number>; // Section ID to weight mapping
  subsectionWeights: Record<string, number>; // Subsection ID to weight mapping
  itemWeights: Record<string, number>; // Item ID to weight mapping
}

// Default template structure mapping to our framework
export interface FrameworkTemplate {
  id: string;
  name: string;
  description: string;
  sections: SectionTemplate[];
}

export interface SectionTemplate {
  name: string;
  description: string;
  defaultWeight: number;
  isCore: boolean;
  subsections: SubsectionTemplate[];
}

export interface SubsectionTemplate {
  name: string;
  description: string;
  defaultWeight: number;
  items: ItemTemplate[];
}

export interface ItemTemplate {
  title: string;
  description: string;
  defaultWeight: number;
  isRequired: boolean;
  guidance?: string;
}

// AI Assistance types
export interface AISuggestion {
  id: string;
  targetId: string; // ID of the item, subsection, or section the suggestion is for
  targetType: "ITEM" | "SUBSECTION" | "SECTION";
  suggestionType: SuggestionType;
  content: string;
  accepted: boolean;
  generatedAt: Date;
}

export enum SuggestionType {
  Completion = "COMPLETION",
  Quality = "QUALITY",
  GapAnalysis = "GAP_ANALYSIS",
  CrossReference = "CROSS_REFERENCE",
  ContentGeneration = "CONTENT_GENERATION"
}

// User preferences
export interface UserPreferences {
  id: string;
  userId: string;
  theme: "light" | "dark";
  aiAssistanceEnabled: boolean;
  aiFeatures: {
    contentSuggestions: boolean;
    qualityChecks: boolean;
    crossReferences: boolean;
    gapAnalysis: boolean;
  };
  defaultWeightProfile: string; // ID of the default weight profile
  defaultTemplate: string; // ID of the default template
}
